import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from '../../../redux';
import { useForm } from 'react-hook-form';
import { ThemeSwitch } from '../../../shared/components/theme-switch';
import { useHttpMutation, useHttpQuery } from '../../../api';
import { API_URLS } from '../consts';
import {
  User,
  updateUserState,
  userInitialState,
} from '../../../redux/reducers';
import { LoggerService } from '../../../shared/logger';
import {
  ErrorLayout,
  LoadingLayout,
  useAlert,
  usePopUpBox,
  UserAvatar,
} from '../../../shared/components';
import { BhabiStorage } from '../../../shared/lib';
import { SIGN_IN_ROUTES } from '../../unauthenticated';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ENV } from '@/shared/env';
import { Trash2, Edit2, Check, X } from 'lucide-react';
import {
  useFileDelete,
  useFileUpload,
  changeToBlob,
  mutateUploadFile,
} from '../../../shared/hooks';
import { Rating, RatingProps } from '@mui/material';
import { UserPublicProfileResponse, RateUserInput } from '../types';
import { Screen, ScreenTabs } from '@/shared/components/ios-screen';

export declare type ProfileInput = {
  name: string;
};

type FileInputs = {
  file: FileList;
};

interface ProfilePageProps {
  id?: string;
}

export const Profile: FC = () => {
  const { email, name, photoURL, id } = useSelector(
    (state: Store) => state.userReducer
  );
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [nameValue, setNameValue] = React.useState(name);
  const [isUploadingFile, setIsUploadingFile] = React.useState(false);
  const [tab, setTab] = useState('details');

  const { mutate: changeName } = useHttpMutation<
    User & { isInRoom: boolean },
    ProfileInput
  >(API_URLS.UPDATE_PROFILE_NAME, 'POST');

  const { mutate: deleteAccount } = useHttpMutation(
    API_URLS.DELETE_ACCOUNT,
    'DELETE'
  );

  const { mutate: deleteProfilePic } = useFileDelete();
  const { mutate: uploadFile, isLoading: isUploadingPhoto } = useFileUpload();

  // Fetch own profile stats
  const { data: userStats } = useHttpQuery<UserPublicProfileResponse>(
    ['userProfileStats'],
    [API_URLS.GET_USER_PUBLIC_PROFILE, id].join('/'),
    'GET',
    undefined,
    {
      enabled: !!id?.length,
    }
  );

  const dispatch = useDispatch();
  const { addAlert } = useAlert();
  const nav = useNavigate();

  const [error, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { isValid },
    reset: resetFileForm,
  } = useForm<FileInputs>({
    mode: 'onSubmit',
  });

  useEffect(() => {
    setIsUploadingFile(isValid);
  }, [isValid]);

  const { addPopUpBox } = usePopUpBox();

  const onDeleteAccount = () => {
    deleteAccount(undefined, {
      onSuccess: async () => {
        addAlert({
          message: 'Account deleted successfully',
          type: 'success',
        });
        await BhabiStorage.removeItem('token');
        await BhabiStorage.removeItem('signedUpFor');
        dispatch(updateUserState({ ...userInitialState, isIdle: false }));
        nav(SIGN_IN_ROUTES.ABSOLUTE_PATH);
      },
      onError: err => {
        LoggerService.error(err);
        addAlert({
          message: 'Error deleting account',
          type: 'error',
        });
      },
    });
  };

  const askToConfirmDelete = () => {
    addPopUpBox(
      {
        message:
          'This action is irreversible and will delete all your data including all rooms and invites.',
        callback: status => {
          if (status) {
            onDeleteAccount();
          }
        },
      },
      {
        texts: {
          title: 'Delete Account?',
          yes: 'Delete',
          no: 'Cancel',
        },
      }
    );
  };

  const onSaveName = () => {
    if (!nameValue || nameValue.trim().length === 0) {
      addAlert({
        message: 'Name cannot be empty',
        type: 'error',
      });
      return;
    }

    changeName(
      { name: nameValue },
      {
        onSuccess: res => {
          dispatch(updateUserState(res));
          setIsEditingName(false);
          addAlert({
            message: 'Profile updated successfully',
            type: 'success',
          });
        },
        onError: err => {
          LoggerService.error(err);
          addAlert({
            message: 'Error updating profile',
            type: 'error',
          });
        },
      }
    );
  };

  const onCancelEditName = () => {
    setNameValue(name);
    setIsEditingName(false);
  };

  const onDeleteProfilePic = useCallback(() => {
    deleteProfilePic(
      {},
      {
        onSuccess: () => {
          dispatch(updateUserState({ photoURL: null }));
          addAlert({
            message: 'Profile photo deleted',
            type: 'success',
          });
        },
        onError: err => {
          LoggerService.error('Error deleting profile photo: ', err);
          addAlert({
            message: 'Error deleting profile photo',
            type: 'error',
          });
        },
      }
    );
  }, [addAlert, deleteProfilePic, dispatch]);

  const onUploadPhoto = handleSubmit(data => {
    mutateUploadFile(data, uploadFile, (res, err) => {
      if (res) {
        addAlert({
          message: 'Profile photo updated',
          type: 'success',
        });

        dispatch(
          updateUserState({
            photoURL: changeToBlob(res.file, res.mimeType),
          })
        );
        resetFileForm();
        setIsUploadingFile(false);
        return;
      }

      LoggerService.error('Profile photo update failed: ', err);

      addAlert({
        message: 'Profile photo update failed',
        type: 'error',
      });
    });
  });

  const fileField = register('file', {
    required: 'Profile photo is required',
    validate: {
      checkFileType: value => {
        const isValidFormat = ['image/jpeg', 'image/jpg', 'image/png'].includes(
          value[0].type
        );

        if (!isValidFormat) {
          setErrorMessage('Only jpg, jpeg, and png formats are supported.');
          return false;
        }

        setErrorMessage('');
        return true;
      },
    },
  });

  const winRate = userStats?.games
    ? Math.round((userStats.wins / userStats.games) * 100)
    : 0;

  const detailsPanel = (
    <div className="bh-panel">
      <section className="bh-ps__photo" aria-labelledby="avatar-title">
        <div className="relative">
          <UserAvatar
            hideState
            fetchCurrentUser
            avatarProps={{
              sx: {
                height: 88,
                width: 88,
                objectFit: 'contain',
              },
            }}
          />
          {photoURL && (
            <button
              type="button"
              onClick={onDeleteProfilePic}
              className="bh-icon-btn bh-profile__remove"
              aria-label="Remove profile photo"
            >
              <Trash2 size={18} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="bh-ps__photo-actions">
          <h2 id="avatar-title" className="bh-h3">
            Photo
          </h2>
          <label className="bh-btn bh-btn--secondary bh-btn--small bh-btn--block bh-file">
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="bh-file__input"
              aria-describedby={error ? 'photo-error' : undefined}
              {...fileField}
            />
            Choose a photo
          </label>
          {isUploadingFile && (
            <button
              type="button"
              onClick={onUploadPhoto}
              disabled={isUploadingPhoto || error.length > 0}
              className="bh-btn bh-btn--primary bh-btn--small bh-btn--block"
            >
              {isUploadingPhoto ? 'Uploading…' : 'Upload photo'}
            </button>
          )}
        </div>
      </section>

      {error.length > 0 && (
        <p id="photo-error" className="bh-error" role="alert">
          {error}
        </p>
      )}

      <div className="bh-ps__divider" aria-hidden="true" />

      <section aria-labelledby="name-title" style={{ display: 'grid', gap: 6 }}>
        <h2 id="name-title" className="bh-h3">
          Name
        </h2>
        {isEditingName ? (
          <form
            className="bh-profile__edit"
            onSubmit={e => {
              e.preventDefault();
              onSaveName();
            }}
          >
            <label htmlFor="profile-name" className="st-sr-only">
              Display name
            </label>
            <input
              id="profile-name"
              type="text"
              value={nameValue}
              autoComplete="name"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onChange={e => setNameValue(e.target.value)}
              className="bh-input"
            />
            <button
              type="submit"
              className="bh-btn bh-btn--primary bh-btn--small"
              aria-label="Save name"
            >
              <Check size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={onCancelEditName}
              className="bh-btn bh-btn--ghost bh-btn--small"
              aria-label="Cancel editing name"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </form>
        ) : (
          <div className="bh-ps__row">
            <p className="bh-ps__value">{name}</p>
            <button
              type="button"
              onClick={() => setIsEditingName(true)}
              className="bh-btn bh-btn--secondary bh-btn--small"
            >
              <Edit2 size={16} aria-hidden="true" />
              Edit
            </button>
          </div>
        )}
      </section>

      <section
        aria-labelledby="email-title"
        style={{ display: 'grid', gap: 4 }}
      >
        <h2 id="email-title" className="bh-h3">
          Email
        </h2>
        <p className="bh-ps__value bh-muted">{email}</p>
      </section>
    </div>
  );

  const statsPanel = (
    <div className="bh-panel">
      <h2 className="st-sr-only">Your game stats</h2>
      {userStats ? (
        <dl className="bh-stat-grid">
          <div>
            <dt>Games</dt>
            <dd>{userStats?.games || 0}</dd>
          </div>
          <div>
            <dt>Win rate</dt>
            <dd>{winRate}%</dd>
          </div>
          <div>
            <dt>Wins</dt>
            <dd>{userStats?.wins || 0}</dd>
          </div>
          <div>
            <dt>Rating</dt>
            <dd>
              {userStats?.rating?.toFixed(1) || '0.0'}{' '}
              <span aria-hidden="true">★</span>
            </dd>
          </div>
        </dl>
      ) : (
        <p className="bh-small bh-muted" role="status">
          Loading your stats…
        </p>
      )}
    </div>
  );

  const settingsPanel = (
    <div className="bh-panel">
      <section className="bh-ps__row" aria-label="Appearance">
        <div>
          <h2 className="bh-h3">Appearance</h2>
          <p className="bh-small bh-muted" style={{ margin: 0 }}>
            Night table or day table.
          </p>
        </div>
        {!ENV.REACT_APP_HIDE_THEME_CHANGE && <ThemeSwitch />}
      </section>

      <div className="bh-ps__divider" aria-hidden="true" />

      <section className="bh-ps__row" aria-labelledby="danger-title">
        <div>
          <h2 id="danger-title" className="bh-h3">
            Delete account
          </h2>
          <p className="bh-small bh-muted" style={{ margin: 0 }}>
            Permanently removes your coins, rooms and invites.
          </p>
        </div>
        <button
          type="button"
          onClick={askToConfirmDelete}
          className="bh-btn bh-btn--danger bh-btn--small"
        >
          <Trash2 size={16} aria-hidden="true" />
          Delete
        </button>
      </section>
    </div>
  );

  return (
    <Screen className="bh-ps" width="narrow">
      <header className="bh-screen__head">
        <p className="bh-eyebrow">Your profile</p>
        <h1 className="bh-screen__title">{name || 'Player'}</h1>
      </header>

      <ScreenTabs
        label="Profile sections"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'details', label: 'Details', content: detailsPanel },
          { id: 'stats', label: 'Stats', content: statsPanel },
          { id: 'settings', label: 'Settings', content: settingsPanel },
        ]}
      />
    </Screen>
  );
};

export const PublicProfile: FC<ProfilePageProps> = ({ id }) => {
  const [params] = useSearchParams();
  const userID = useMemo(() => id || params.get('user_id'), [params, id]);

  const [userProfileURL, setUserProfileURL] = React.useState<string>('');
  const currentUser = useSelector((state: Store) => state.userReducer);
  const [rating, setRating] = React.useState<number | null>(0);
  const [tab, setTab] = useState('rating');
  const { addAlert } = useAlert();

  const { mutate: rateUser } = useHttpMutation<
    { message: string },
    RateUserInput
  >(API_URLS.RATE_USER, 'POST');

  React.useEffect(() => {
    if (userID?.length || !userProfileURL.length) {
      setUserProfileURL([API_URLS.GET_USER_PUBLIC_PROFILE, userID].join('/'));
    }
  }, [userProfileURL, userID]);

  const {
    data: user,
    isLoading,
    isError,
    refetch,
  } = useHttpQuery<UserPublicProfileResponse>(
    ['userPublicProfile'],
    userProfileURL,
    'GET',
    undefined,
    {
      enabled: !!userProfileURL?.length,
    }
  );

  const onChangeRating: RatingProps['onChange'] = (_, newValue) => {
    if (!userID || !newValue) {
      return;
    }

    setRating(newValue);

    rateUser(
      {
        userID,
        rating: newValue,
      },
      {
        onSuccess: () => {
          refetch();
          addAlert({
            type: 'success',
            message: `${user?.name || 'user'} is rated ${newValue} stars`,
          });
        },
        onError: err => {
          LoggerService.error('Rate user failed', err);
          addAlert({
            type: 'error',
            message: 'Rate user failed',
          });
        },
      }
    );
  };

  if (!userID) {
    return (
      <ErrorLayout
        texts={{ defaultError: 'We couldn’t find that player.' }}
        outerBoxProps={{ height: 'auto', minHeight: '50vh' }}
      />
    );
  }

  if (isLoading && !isError) {
    return <LoadingLayout inline text="Pulling up their profile…" />;
  }

  if (isError) {
    return (
      <ErrorLayout
        texts={{ defaultError: 'Couldn’t load this profile. Try again.' }}
        outerBoxProps={{ height: 'auto', minHeight: '50vh' }}
      />
    );
  }

  const isOwnProfile = currentUser?.id === userID;
  const winRate = user?.games ? Math.round((user.wins / user.games) * 100) : 0;

  return (
    <Screen className="bh-ps" width="narrow">
      <header className="bh-ps__head">
        <UserAvatar
          name={user?.name || ''}
          avatarProps={{
            sx: {
              height: 56,
              width: 56,
              objectFit: 'contain',
            },
          }}
          photoURL={user?.photoURL}
          userState={user?.state}
        />
        <div className="bh-screen__head">
          <p className="bh-eyebrow">Player profile</p>
          <h1 className="bh-screen__title">{user?.name}</h1>
          {user?.email && (
            <p className="bh-screen__sub bh-ps__value">{user.email}</p>
          )}
        </div>
      </header>

      <ScreenTabs
        label="Player sections"
        value={tab}
        onChange={setTab}
        tabs={[
          {
            id: 'rating',
            label: 'Rating',
            content: (
              <section
                className="bh-panel"
                aria-label="Rating"
                style={{ textAlign: 'center', justifyItems: 'center' }}
              >
                <h2 className="bh-h3" id="rate-title">
                  {isOwnProfile ? 'Your rating' : 'Rate this player'}
                </h2>
                {!isOwnProfile ? (
                  <Rating
                    value={rating}
                    onChange={onChangeRating}
                    size="large"
                    aria-labelledby="rate-title"
                  />
                ) : (
                  <Rating
                    value={user?.rating || 0}
                    readOnly
                    size="large"
                    aria-labelledby="rate-title"
                  />
                )}
                <p className="bh-small bh-muted">
                  Average{' '}
                  <strong style={{ color: 'var(--bh-text)' }}>
                    {user?.rating?.toFixed(1) || '0.0'}
                  </strong>{' '}
                  <span aria-hidden="true">★</span>
                </p>
              </section>
            ),
          },
          {
            id: 'stats',
            label: 'Stats',
            content: (
              <section className="bh-panel" aria-label="Game stats">
                <dl className="bh-stat-grid">
                  <div>
                    <dt>Games played</dt>
                    <dd>{user?.games || 0}</dd>
                  </div>
                  <div>
                    <dt>Wins</dt>
                    <dd>{user?.wins || 0}</dd>
                  </div>
                  <div>
                    <dt>Losses</dt>
                    <dd>{user?.lost || 0}</dd>
                  </div>
                  <div>
                    <dt>Win rate</dt>
                    <dd>{winRate}%</dd>
                  </div>
                </dl>
              </section>
            ),
          },
        ]}
      />
    </Screen>
  );
};

export const ProfilePage: FC = () => {
  return <Profile />;
};

export const OtherUserProfilePage: FC<ProfilePageProps> = ({ id }) => {
  return <PublicProfile id={id} />;
};
